import { c as _objectWithoutProperties, j as _toConsumableArray, d as _objectSpread2 } from '../_helpers/_rollupPluginBabelHelpers.js';
import 'core-js/modules/es.array.filter.js';
import 'core-js/modules/es.object.to-string.js';
import { forwardRef, useRef, useImperativeHandle, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isNodeInVisiblePartOfPage } from '../global/dom.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import 'core-js/modules/es.array.iterator.js';
import 'core-js/modules/es.object.assign.js';
import 'core-js/modules/es.object.entries.js';
import 'core-js/modules/es.regexp.exec.js';
import 'core-js/modules/es.set.js';
import 'core-js/modules/es.string.split.js';
import 'core-js/modules/web.dom-collections.for-each.js';
import 'core-js/modules/web.dom-collections.iterator.js';

var modules_6dce58ae = {"light":"light_rui_2ac4","trapButton":"trapButton_rui_11f0"};

var _excluded = ["children", "trapDisabled", "autoFocusFirst", "focusBackOnClose", "focusBackOnExit"];
var FOCUSABLE_ELEMENTS = 'input, button, select, textarea, a[href], *[tabindex]:not([data-trap-button]):not([data-scrollable-container])';
// eslint-disable-next-line @typescript-eslint/no-shadow
var TabTrap = /*#__PURE__*/forwardRef(function TabTrap(_ref, ref) {
  var children = _ref.children,
    _ref$trapDisabled = _ref.trapDisabled,
    trapDisabled = _ref$trapDisabled === void 0 ? false : _ref$trapDisabled,
    _ref$autoFocusFirst = _ref.autoFocusFirst,
    autoFocusFirst = _ref$autoFocusFirst === void 0 ? true : _ref$autoFocusFirst,
    _ref$focusBackOnClose = _ref.focusBackOnClose,
    focusBackOnClose = _ref$focusBackOnClose === void 0 ? true : _ref$focusBackOnClose,
    _ref$focusBackOnExit = _ref.focusBackOnExit,
    focusBackOnExit = _ref$focusBackOnExit === void 0 ? false : _ref$focusBackOnExit,
    restProps = _objectWithoutProperties(_ref, _excluded);
  var nodeRef = useRef(null);
  var trapButtonNodeRef = useRef(null);
  var previousFocusedNodeRef = useRef(null);
  var trapWithoutFocusRef = useRef(false);
  var mountedRef = useRef(false);
  // It's the same approach as in focus-trap-react:
  // https://github.com/focus-trap/focus-trap-react/commit/3b22fca9eebeb883edc89548850fe5a5b9d6d50e
  // We can't do it in useEffect because it's too late, some children might have already
  // focused itself.
  if (previousFocusedNodeRef.current === null) {
    previousFocusedNodeRef.current = document.activeElement;
  }
  useImperativeHandle(ref, function () {
    return {
      node: nodeRef.current
    };
  }, []);
  var focusFirst = useCallback(function () {
    return focusElement(true);
  }, []);
  var focusLast = function focusLast() {
    return focusElement(false);
  };
  useEffect(function () {
    mountedRef.current = true;
    return function () {
      mountedRef.current = false;
    };
  }, []);
  useEffect(function () {
    if (autoFocusFirst) {
      focusFirst();
    } else if (!trapDisabled) {
      var _nodeRef$current, _nodeRef$current2;
      var previousFocusedElementIsInContainer = previousFocusedNodeRef.current && ((_nodeRef$current = nodeRef.current) === null || _nodeRef$current === void 0 ? void 0 : _nodeRef$current.contains(previousFocusedNodeRef.current));
      // The component wrapped in TabTrap can already have a focused element (e.g. Date Picker),
      // so we need to check if it does. If so, we don't need to focus anything.
      var currentlyFocusedElementIsInContainer = (_nodeRef$current2 = nodeRef.current) === null || _nodeRef$current2 === void 0 ? void 0 : _nodeRef$current2.contains(document.activeElement);
      if (!nodeRef.current || !previousFocusedElementIsInContainer && !currentlyFocusedElementIsInContainer) {
        var _trapButtonNodeRef$cu;
        trapWithoutFocusRef.current = true;
        (_trapButtonNodeRef$cu = trapButtonNodeRef.current) === null || _trapButtonNodeRef$cu === void 0 || _trapButtonNodeRef$cu.focus();
      }
    }
    return function () {
      if (focusBackOnClose) {
        restoreFocus();
      }
    };
  }, [autoFocusFirst, trapDisabled, focusBackOnClose, focusFirst]);
  function restoreFocus() {
    var previousFocusedNode = previousFocusedNodeRef.current;
    if (previousFocusedNode instanceof HTMLElement && previousFocusedNode.focus && isNodeInVisiblePartOfPage(previousFocusedNode)) {
      // This is to prevent the focus from being restored the first time
      // componentWillUnmount is called in StrictMode.
      if (!mountedRef.current) {
        previousFocusedNode.focus({
          preventScroll: true
        });
      }
    }
  }
  function focusElement() {
    var first = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    var node = nodeRef.current;
    if (!node) {
      return;
    }
    var tabables = _toConsumableArray(node.querySelectorAll(FOCUSABLE_ELEMENTS)).filter(function (item) {
      return item.tabIndex >= 0;
    });
    var toBeFocused = first ? tabables[0] : tabables[tabables.length - 1];
    if (toBeFocused) {
      toBeFocused.focus();
    }
  }
  function focusLastIfEnabled(event) {
    if (trapWithoutFocusRef.current) {
      return;
    }
    if (focusBackOnExit) {
      var prevFocused = event.nativeEvent.relatedTarget;
      if (prevFocused != null && nodeRef.current != null && prevFocused instanceof Element && nodeRef.current.contains(prevFocused)) {
        restoreFocus();
      }
    } else {
      focusLast();
    }
  }
  function handleBlurIfWithoutFocus(event) {
    var _nodeRef$current3;
    if (!trapWithoutFocusRef.current) {
      return;
    }
    trapWithoutFocusRef.current = false;
    var newFocused = event.nativeEvent.relatedTarget;
    if (!newFocused) {
      return;
    }
    if (newFocused instanceof Element && (_nodeRef$current3 = nodeRef.current) !== null && _nodeRef$current3 !== void 0 && _nodeRef$current3.contains(newFocused)) {
      return;
    }
    focusLast();
  }
  if (trapDisabled) {
    return /*#__PURE__*/jsx("div", _objectSpread2(_objectSpread2({
      ref: nodeRef
    }, restProps), {}, {
      children: children
    }));
  }
  return /*#__PURE__*/jsxs("div", _objectSpread2(_objectSpread2({
    ref: nodeRef
  }, restProps), {}, {
    children: [/*#__PURE__*/jsx("div", {
      // It never actually stays focused
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex: 0,
      ref: trapButtonNodeRef,
      className: modules_6dce58ae.trapButton,
      onFocus: focusLastIfEnabled,
      onBlur: handleBlurIfWithoutFocus,
      "data-trap-button": true
    }), children, /*#__PURE__*/jsx("div", {
      // It never actually stays focused
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex: 0,
      onFocus: focusBackOnExit ? restoreFocus : focusFirst,
      "data-trap-button": true
    })]
  }));
});
TabTrap.propTypes = {
  children: PropTypes.node.isRequired,
  trapDisabled: PropTypes.bool,
  autoFocusFirst: PropTypes.bool,
  focusBackOnClose: PropTypes.bool,
  focusBackOnExit: PropTypes.bool
};

export { FOCUSABLE_ELEMENTS, TabTrap as default };
